import { useCallback, useState, useContext } from "react";

import { exportArchiveGridToExcel } from "./GridExcelExportMenuItemCompat";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import DownloadIcon from "@mui/icons-material/Download";
import MenuIcon from "@mui/icons-material/Menu";
import TableChartIcon from "@mui/icons-material/TableChart";
import {
	Box,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import {
	GridRowId,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	useGridApiContext,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";



dayjs.extend(utc);
dayjs.extend(timezonePlugin);

/** Custom toolbar props; passed via slotProps.toolbar (cast in parent to satisfy grid types). */
type CustomToolbarProps = {
	csvFileName?: string;
	excelFileName?: string;
	timezone?: string;
	rowCount?: number;
};

type CommentEntry = {
	Date?: string;
	Username?: string;
	Comment?: string;
};

type ArchiveExportRow = Record<string, unknown> & {
	Checked?: string;
	Status?: unknown;
	Comments?: CommentEntry[];
	CheckpointId?: string | number;
	ChecklistId?: string | number;
	NodeId?: string | number;
};

type CsvExportColumn = {
	field: string;
	headerName?: string;
	disableExport?: boolean;
	valueGetter?: (params: {
		id: string;
		row: ArchiveExportRow;
		field: string;
		value: unknown;
		getValue: (id: string, field: string) => unknown;
	}) => unknown;
	valueFormatter?: (params: {
		id: string;
		field: string;
		value: unknown;
		row: ArchiveExportRow;
		api: null;
	}) => unknown;
};

function toArchiveRow(value: unknown): ArchiveExportRow {
	if (!value || typeof value !== "object") return {};
	return value as ArchiveExportRow;
}

const isUnset = (raw?: string) =>
	!raw || raw === "0001-01-01T00:00:00" || raw.startsWith("0001-01-01");

const formatCheckedCsv = (row: ArchiveExportRow, tz: string) => {
	const raw = row.Checked;
	const show = row.Status && raw && !isUnset(raw);
	return show ? dayjs.utc(raw).tz(tz).format("DD/MM/YY HH:mm:ss") : "—";
};

const formatCommentsCsv = (row: ArchiveExportRow, tz: string) => {
	const list = Array.isArray(row.Comments) ? row.Comments : [];
	if (!list.length) return "";
	return list
		.map((entry) => {
			const when =
				entry.Date && !isUnset(entry.Date)
					? dayjs.utc(entry.Date).tz(tz).format("DD/MM/YY HH:mm:ss")
					: "—";
			const who = entry.Username ?? "";
			const txt = entry.Comment ?? "";
			return `(${txt}${who ? `, ${who}` : ""}, ${when})`;
		})
		.join(" | ");
};

const csvEscape = (v: unknown) => {
	const s =
		v == null
			? ""
			: typeof v === "string"
				? v
				: typeof v === "number" || typeof v === "boolean"
					? String(v)
					: JSON.stringify(v);
	if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
	return s;
};

function isCsvColumn(value: unknown): value is CsvExportColumn {
	if (!value || typeof value !== "object") return false;
	const rec = value as Record<string, unknown>;
	return typeof rec.field === "string";
}

/** Export nodeList to CSV without grid API (e.g. for mobile card view). */
export function exportArchiveDataToCsv(
	nodeList: ArchiveExportRow[],
	columns: CsvExportColumn[],
	fileName: string,
	tz: string,
) {
	const cols = columns.filter((c) => !c.disableExport);
	const headers = cols.map((c) => csvEscape(c.headerName ?? c.field)).join(",");
	const lines = nodeList.map((row) => {
		const id = `${row.CheckpointId ?? ""}-${row.ChecklistId ?? ""}-${row.NodeId ?? ""}`;
		const cells = cols.map((c) => {
			if (c.field === "Checked") return csvEscape(formatCheckedCsv(row, tz));
			if (c.field === "Comments") return csvEscape(formatCommentsCsv(row, tz));
			let v = row[c.field];
			if (typeof c.valueGetter === "function") {
				v = c.valueGetter({
					id,
					row,
					field: c.field,
					value: v,
					getValue: (_, field) => row[field],
				});
			}
			if (typeof c.valueFormatter === "function") {
				const fmt = c.valueFormatter({
					id,
					field: c.field,
					value: v,
					row,
					api: null,
				});
				if (fmt != null) return csvEscape(fmt);
			}
			if (Array.isArray(v)) return csvEscape(v.join(" | "));
			return csvEscape(v);
		});
		return cells.join(",");
	});
	const csv = [headers, ...lines].join("\n");
	const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = `${fileName || "export"}.csv`;
	a.click();
	URL.revokeObjectURL(url);
}

function doCsvExport(
	api: ReturnType<typeof useGridApiContext>["current"] | null,
	fileName: string,
	tz: string,
) {
	if (!api) return;

	const rawCols = api.getVisibleColumns?.() ?? api.getAllColumns?.() ?? [];
	const cols = rawCols.filter(isCsvColumn);
	const rowIds: GridRowId[] =
		api.getSortedRowIds?.() ?? Array.from(api.getRowModels?.().keys() ?? []);

	const headers = cols.map((c) => csvEscape(c.headerName ?? c.field)).join(",");
	const lines = rowIds.map((id) => {
		const rowModel = toArchiveRow(api.getRow(id) as unknown);
		const cells = cols.map((c) => {
			const field = c.field;
			if (field === "Checked") return csvEscape(formatCheckedCsv(rowModel, tz));
			if (field === "Comments")
				return csvEscape(formatCommentsCsv(rowModel, tz));
			const cellParams = api.getCellParams?.(id, field) as
				| { formattedValue?: unknown; value?: unknown }
				| undefined;
			const v =
				cellParams?.formattedValue ??
				cellParams?.value ??
				rowModel[field] ??
				"";
			if (Array.isArray(v)) return csvEscape(v.join(" | "));
			return csvEscape(v);
		});
		return cells.join(",");
	});

	const csv = [headers, ...lines].join("\n");
	const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = `${fileName || "export"}.csv`;
	a.click();
	URL.revokeObjectURL(url);
}

function ExportCsvButton({ fileName, tz }: { fileName: string; tz: string }) {
	const apiRef = useGridApiContext();
	const handleExport = useCallback(() => {
		doCsvExport(apiRef.current, fileName, tz);
	}, [apiRef, fileName, tz]);

	return (
		<ProcedureButton
			variant="outlined"
			color="primary"
			size="small"
			startIcon={<DownloadIcon />}
			onClick={handleExport}
			sx={{ textTransform: "none", fontWeight: 700 }}
		>
			<Translate String="Auto.Shared.DataGrid.CustomToolBar.L233C4.ExportCSV">Export CSV</Translate>
		</ProcedureButton>
	);
}

function ExportExcelButton({
	fileName,
	sheetName,
}: {
	fileName: string;
	sheetName?: string;
}) {
	const apiRef = useGridApiContext();
	const handleExport = useCallback(() => {
		void exportArchiveGridToExcel(apiRef, {
			fileName,
			sheetName: sheetName ?? "Archive data",
		});
	}, [apiRef, fileName, sheetName]);

	return (
		<ProcedureButton
			variant="contained"
			color="primary"
			size="small"
			startIcon={<TableChartIcon />}
			onClick={handleExport}
			sx={{ textTransform: "none", fontWeight: 700 }}
		>
			<Translate String="Auto.Shared.DataGrid.CustomToolBar.L262C4.ExportExcel">Export Excel</Translate>
		</ProcedureButton>
	);
}

export const CustomToolbar = ({
	csvFileName = "archive",
	excelFileName = "archive",
	timezone = "UTC",
	rowCount,
}: CustomToolbarProps) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const apiRef = useGridApiContext();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const open = Boolean(anchorEl);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => setAnchorEl(null);

	const handleExportCsv = useCallback(() => {
		doCsvExport(apiRef.current, csvFileName, timezone);
		handleClose();
	}, [apiRef, csvFileName, timezone]);

	if (isSmallScreen) {
		return (
			<>
				<IconButton
					size="small"
					color="primary"
					aria-label={GetLanguageValueAsString(translation, "Auto.Shared.DataGrid.CustomToolBar.L299C17.ToolbarMenu", "Toolbar menu")}
					title={GetLanguageValueAsString(translation, "Auto.Shared.DataGrid.CustomToolBar.L300C12.ColumnsDensityExport", "Columns, density, export")}
					onClick={handleClick}
					sx={{ width: 40, height: 40 }}
				>
					<MenuIcon />
				</IconButton>
				<Menu
					open={open}
					anchorEl={anchorEl}
					onClose={handleClose}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
					slotProps={{
						paper: { sx: { borderRadius: 2, mt: 1.5, minWidth: 220 } },
					}}
				>
					<Box sx={{ py: 1 }}>
						<MenuItem onClick={handleClose} sx={{ gap: 1 }}>
							<GridToolbarColumnsButton />
							<ListItemText primary={GetLanguageValueAsString(translation, "Auto.Shared.DataGrid.CustomToolBar.L319C30.Columns", "Columns")} />
						</MenuItem>
						<MenuItem onClick={handleClose} sx={{ gap: 1 }}>
							<GridToolbarDensitySelector />
							<ListItemText primary={GetLanguageValueAsString(translation, "Auto.Shared.DataGrid.CustomToolBar.L323C30.Density", "Density")} />
						</MenuItem>
						<MenuItem onClick={handleExportCsv} sx={{ gap: 1 }}>
							<ListItemIcon>
								<DownloadIcon fontSize="small" color="primary" />
							</ListItemIcon>
							<ListItemText primary={GetLanguageValueAsString(translation, "Auto.Shared.DataGrid.CustomToolBar.L329C30.ExportAsCSV", "Export as CSV")} />
						</MenuItem>
						<MenuItem
							onClick={() => {
								void exportArchiveGridToExcel(apiRef, {
									fileName: excelFileName,
									sheetName: "Archive data",
								});
								handleClose();
							}}
							sx={{ gap: 1 }}
						>
							<ListItemIcon>
								<TableChartIcon fontSize="small" color="primary" />
							</ListItemIcon>
							<ListItemText primary={GetLanguageValueAsString(translation, "Auto.Shared.DataGrid.CustomToolBar.L344C30.ExportAsExcel", "Export as Excel")} />
						</MenuItem>
					</Box>
				</Menu>
			</>
		);
	}

	return (
		<GridToolbarContainer
			sx={{
				gap: 1,
				p: 1.25,
				borderBottom: "1px solid",
				borderColor: "divider",
				flexWrap: "wrap",
				alignItems: "center",
			}}
		>
			<GridToolbarColumnsButton />
			<GridToolbarFilterButton />
			<GridToolbarDensitySelector />
			{rowCount != null && (
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ fontWeight: 600, ml: 0.5 }}
				>
					{rowCount} {rowCount === 1 ? "row" : "rows"}
				</Typography>
			)}
			<Box sx={{ flex: 1 }} />
			<ExportCsvButton fileName={csvFileName} tz={timezone} />
			<ExportExcelButton fileName={excelFileName} sheetName="Archive data" />
		</GridToolbarContainer>
	);
};
